import { Bell } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { useQuery, useAction } from "wasp/client/operations";
import { getMyMentorNotifications, markMentorNotificationsRead } from "wasp/client/operations";
import { Button } from "../client/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../client/components/ui/dropdown-menu";
import useTranslation from "../client/hooks/useTranslation";
import { useLanguageContext } from "../client/contexts/LanguageContext";
import { NOTIFICATION_TYPE } from "./types";

// ── Types ──────────────────────────────────────────────────────────────────────

interface MentorNotificationItem {
  id: string;
  type: string;
  isRead: boolean;
  createdAt: string | Date;
  studentName: string | null;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function NotificationBell() {
  const t = useTranslation();
  const { language } = useLanguageContext();

  const { data: rawNotifications, refetch } = useQuery(getMyMentorNotifications, undefined, {
    refetchInterval: 30_000,
  });

  const markReadFn = useAction(markMentorNotificationsRead);

  const items = (rawNotifications as MentorNotificationItem[] | undefined) ?? [];
  const unreadCount = items.filter((n) => !n.isRead).length;

  async function handleMarkAllRead() {
    const unreadIds = items.filter((n) => !n.isRead).map((n) => n.id);
    if (!unreadIds.length) return;
    await markReadFn({ notificationIds: unreadIds });
    refetch();
  }

  async function handleMarkRead(id: string) {
    await markReadFn({ notificationIds: [id] });
    refetch();
  }

  function getNotificationLabel(type: string): string {
    switch (type) {
      case NOTIFICATION_TYPE.INVITATION_RECEIVED:
        return t.mentor.notifications.invitationReceived;
      case NOTIFICATION_TYPE.INVITATION_ACCEPTED:
        return t.mentor.notifications.invitationAccepted;
      case NOTIFICATION_TYPE.INVITATION_DECLINED:
        return t.mentor.notifications.invitationDeclined;
      case NOTIFICATION_TYPE.RELATION_REVOKED:
        return t.mentor.notifications.relationRevoked;
      case NOTIFICATION_TYPE.MENTOR_NOTE_ADDED:
        return t.mentor.notifications.noteAdded;
      default:
        return type;
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative h-9 w-9 p-0"
          aria-label={t.mentor.notifications.markAllRead}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              aria-label={`${unreadCount} notifications non lues`}
              className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-white"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-sm font-semibold">{t.mentor.title}</span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={handleMarkAllRead}
            >
              {t.mentor.notifications.markAllRead}
            </Button>
          )}
        </div>

        <DropdownMenuSeparator />

        {/* Notification list */}
        {items.length === 0 ? (
          <p className="px-3 py-4 text-center text-sm text-muted-foreground">
            {t.mentor.notifications.noNotifications}
          </p>
        ) : (
          items.slice(0, 10).map((n) => (
            <DropdownMenuItem
              key={n.id}
              className={`flex flex-col items-start gap-0.5 px-3 py-2 cursor-pointer ${
                !n.isRead ? "bg-muted/40" : ""
              }`}
              onClick={() => {
                if (!n.isRead) handleMarkRead(n.id);
              }}
            >
              <span className={`text-sm leading-snug ${!n.isRead ? "font-medium" : ""}`}>
                {n.studentName && <span className="font-semibold">{n.studentName} </span>}
                {getNotificationLabel(n.type)}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(n.createdAt), {
                  addSuffix: true,
                  locale: language === "fr" ? fr : undefined,
                })}
              </span>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
